import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Panel,
  Grid,
  Row,
  Col,
  Clearfix,
  FormControl,
  ControlLabel,
  Checkbox,
  Button,
} from 'react-bootstrap';
import DeviceRotationControl from './DeviceRotationControl';
import PowerSchedules from './PowerSchedules';
import TagCloud from '../TagCloud/TagCloud';
import { checkPathAccess } from '../../utils/checkPathAccess';
import { getPermissions } from '../../utils/getPermissions';
import ToolTip from '../ToolTipIcon/ToolTip';
import { DeviceType } from './utils/DeviceType';

export const validateMulticastAddress = candidate => {
  const value = candidate == null ? '' : String(candidate).trim();

  // when it left empty, currently active remains
  if (!value) {
    return true;
  }

  // IPv4 multicast: 224.0.0.0–239.255.255.255
  const ipv4Pattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const m = ipv4Pattern.exec(value);

  if (m) {
    const [, aStr, bStr, cStr, dStr] = m;
    const a = Number(aStr);
    const b = Number(bStr);
    const c = Number(cStr);
    const d = Number(dStr);
    const isFirstOctetMulticast = a >= 224 && a <= 239;
    const areRemainingOctetsValid = [b, c, d].every(n => n >= 0 && n <= 255);

    return isFirstOctetMulticast && areRemainingOctetsValid;
  }

  // IPv6 multicast: ff00::/8; require non-zero scope (last nibble of first hextet != 0)
  const s = value.toLowerCase();

  if (!s.startsWith('ff')) {
    return false;
  }

  // only hex + colons
  if (!/^[\da-f:]+$/.test(s)) {
    return false;
  }

  // at most one compression
  if ((s.match(/::/g) || []).length > 1) {
    return false;
  }

  const [leftStr, rightStr] = s.split('::');
  const left = leftStr ? leftStr.split(':') : [];
  const right = rightStr ? rightStr.split(':') : [];
  const isHex1to4 = h => h.length >= 1 && h.length <= 4 && /^[\da-f]+$/.test(h);

  // first hextet must exist and be ffXX with scope != 0
  if (left.length === 0 || !isHex1to4(left[0])) {
    return false;
  }

  if (!/^ff[\da-f]{2}$/.test(left[0])) {
    return false;
  }

  // scope must be non-zero
  if (left[0][3] === '0') {
    return false;
  }

  if (!left.every(isHex1to4) || !right.every(isHex1to4)) {
    return false;
  }

  // no '::' → exactly 8 hextets
  if (rightStr === undefined) {
    return left.length === 8;
  }

  // with '::' → total hextets must be < 8 (at least one compressed)
  return left.length + right.length < 8;
};

export const validateMulticastPort = candidate => {
  const value = candidate === null || candidate === undefined ? '' : String(candidate).trim();

  if (!value) {
    return true; // Allow empty - backend will use default
  }

  if (!/^\d+$/.test(value)) {
    return false;
  }

  const portNum = Number.parseInt(value, 10);

  return portNum >= 1 && portNum <= 65535;
};

export default class DeviceForm extends Component {
  state = {
    isMulticastAddressValid: validateMulticastAddress(this.props.device.multicast_address),
    isMulticastPortValid: validateMulticastPort(this.props.device.multicast_port),
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextIpValid = validateMulticastAddress(nextProps.device.multicast_address);
    const nextPortValid = validateMulticastPort(nextProps.device.multicast_port);
    const updates = {};

    if (nextIpValid !== prevState.isMulticastAddressValid) {
      updates.isMulticastAddressValid = nextIpValid;
    }

    if (nextPortValid !== prevState.isMulticastPortValid) {
      updates.isMulticastPortValid = nextPortValid;
    }

    return Object.keys(updates).length ? updates : null;
  }

  submit = event => {
    const { onUpdate } = this.props;

    event.preventDefault();
    onUpdate();
  };

  componentDidMount() {
    if (window) {
      this.resizeDescriptionTextarea();
      window.addEventListener('resize', this.resizeDescriptionTextarea);
    }
  }

  componentWillUnmount() {
    if (window) {
      window.removeEventListener('resize', this.resizeDescriptionTextarea);
    }
  }

  onChange = event => {
    const { onChange } = this.props;

    onChange(event.target.name, event.target.value);
  };

  onGeoCountryCode = event => {
    const { onChange } = this.props;
    const { name, value } = event.target;
    const normalized = String(value || '')
      .toUpperCase()
      .slice(0, 2);

    onChange(name, normalized);
  };

  onGeoLocationMode = event => {
    const { onChange } = this.props;
    const mode = event.target.value;

    onChange('geo_location_mode', mode);

    if (mode === 'address') {
      onChange('geo_postal_code', '');
    } else {
      onChange('geo_address_line', '');
    }
  };

  onChangeTag = tags => {
    const { onChange } = this.props;

    onChange('tags', tags);
  };

  onChangeCheckbox = event => {
    // every checkbox use 0|1 in the JSON
    const { onChange } = this.props;
    const { name, checked } = event.target;
    const value = checked ? 1 : 0;

    onChange(name, value);
  };

  onChangeSelect = event => {
    // every select use numbers in the JSON
    const { onChange } = this.props;
    const { name, value } = event.target;

    onChange(name, Number.parseInt(value, 10));
  };

  onChangeTextarea = event => {
    // max description length in the db is 255
    if (event.target.value.length > 255) {
      return;
    }

    this.onChange(event);
    this.resizeDescriptionTextarea();
  };

  onMulticastAddress = event => {
    const { onChange } = this.props;
    const { name, value } = event.target;
    const isMulticastAddressValid = validateMulticastAddress(value);

    this.setState({ isMulticastAddressValid });
    onChange(name, value);
  };

  onMulticastPort = event => {
    const { onChange } = this.props;
    const { name, value } = event.target;
    const isMulticastPortValid = validateMulticastPort(value);

    this.setState({ isMulticastPortValid });
    onChange(name, value);
  };

  resizeDescriptionTextarea = () => {
    if (!this.description) {
      return;
    }

    this.description.style.height = 'auto'; // reset height to recalculate scrollHeight
    this.description.style.height = `${this.description.scrollHeight}px`;
  };

  getDownloadIntervalOptions = () => {
    const texts = ['Short', 'Medium', 'Long'];

    return [30, 60, 120].map((item, index) => (
      <option key={index} value={item}>
        {texts[index]} - {item} seconds
      </option>
    ));
  };

  getFpsOptions = () =>
    [0, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map((item, index) => (
      <option key={index} value={item}>
        {index ? `${item} FPS` : 'Unlimited'}
      </option>
    ));

  getSyncMethodOptions = () =>
    ['None', 'NTP', 'Parent', 'Child'].map((item, index) => (
      <option key={index} value={index}>
        {item}
      </option>
    ));

  getOverlaySourceOptions = () =>
    ['None', 'Qmatic', 'KeyPress', 'Demo'].map((item, index) => (
      <option key={index} value={index}>
        {item}
      </option>
    ));

  getOverlayAlignmentOptions = () =>
    ['None', 'Centre', 'Right'].map((item, index) => (
      <option key={index} value={index}>
        {item}
      </option>
    ));

  getFusionAudioSourceOptions = () =>
    ['SignStix Player', 'HDMI Input'].map((item, index) => (
      <option key={index} value={index}>
        {item}
      </option>
    ));

  getLocation = () => {
    const { getFullLocation, device } = this.props;
    const location = getFullLocation(device);

    return location || 'No Location set';
  };

  getGeneral = () => {
    const { auth, original, device, device_type, disabled, onShowLocationSelector, userLocation } =
      this.props;
    const isAccessAllowed = checkPathAccess(userLocation, device.location_path);

    return (
      <Col md={6} lg={4}>
        <Panel data-test="general-panel">
          <Panel.Heading>General</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>Name</ControlLabel>
              <FormControl
                data-test="input-desc"
                name="description"
                value={device.description}
                onChange={this.onChange}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Description</ControlLabel>
              <FormControl
                data-test="input-desc-2"
                name="description_2"
                componentClass="textarea"
                rows={1}
                value={device.description_2 || ''}
                onChange={this.onChangeTextarea}
                disabled={disabled}
                inputRef={ref => {
                  this.description = ref;
                }}
              />
            </div>
            <div data-test="tags" className={`device-tags ${disabled ? '' : 'edit'}`}>
              <ControlLabel>Tags</ControlLabel>
              <TagCloud
                hideTagsText
                tags={device.tags}
                onChange={this.onChangeTag}
                disabled={disabled}
              />
              <div className="clearfix" />
            </div>
            <div>
              <ControlLabel>Location</ControlLabel>
              <FormControl
                data-test="input-location"
                value={this.getLocation()}
                onClick={onShowLocationSelector}
                disabled={disabled || !isAccessAllowed}
                readOnly
              />
            </div>
            <div>
              <ControlLabel>Download Interval</ControlLabel>
              <select
                data-test="input-downloadinterval"
                name="download_interval"
                value={device.download_interval}
                onChange={this.onChangeSelect}
                disabled={disabled}
              >
                {this.getDownloadIntervalOptions()}
              </select>
            </div>
            <DeviceRotationControl
              hardware_rotation={device.hardware_rotation}
              software_rotation={device.rotation}
              device_type={device_type}
              onChangeSelect={this.onChangeSelect}
              viewMode={disabled}
            />
            <div>
              <ControlLabel>Draw at Top Left</ControlLabel>
              <Checkbox
                data-test="checkbox-drawtopleft"
                name="viewport_top_left"
                checked={device.viewport_top_left}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Max Frame Rate</ControlLabel>
              <select
                data-test="select-fps"
                name="fps_limit"
                value={device.fps_limit}
                onChange={this.onChangeSelect}
                disabled={disabled}
              >
                {this.getFpsOptions()}
              </select>
            </div>
            <div>
              <ControlLabel>Default Device</ControlLabel>
              <Checkbox
                data-test="checkbox-default"
                name="is_default"
                checked={device.is_default || original.is_default}
                onChange={this.onChangeCheckbox}
                disabled={disabled || Boolean(original.is_default)}
              />
            </div>
            <div>
              <ControlLabel>Prestaged Device</ControlLabel>
              <Checkbox
                data-test="checkbox-prestaged"
                name="prestaged"
                checked={device.prestaged}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            {Boolean(auth.user.commander_enabled) && (
              <div>
                <ControlLabel>Commander</ControlLabel>
                <Checkbox
                  data-test="checkbox-commander"
                  name="commander_enabled"
                  checked={device.commander_enabled}
                  onChange={this.onChangeCheckbox}
                  disabled={disabled}
                />
              </div>
            )}
            <div>
              <ControlLabel>
                <ToolTip toolText="Locks the player settings screen. One PIN per client." infoIcon>
                  PIN
                </ToolTip>
              </ControlLabel>
              <span data-test="text-pin">{device.pin || 'Not set'}</span>
            </div>
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  getVideoWall = () => {
    const { device, disabled } = this.props;

    return (
      <Col md={6} lg={4}>
        <Panel>
          <Panel.Heading>Video Wall</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>Sync Method</ControlLabel>
              <select
                data-test="select-syncmethod"
                name="synchronisation_method"
                value={device.synchronisation_method}
                onChange={this.onChangeSelect}
                disabled={disabled}
              >
                {this.getSyncMethodOptions()}
              </select>
            </div>
            <div>
              <ControlLabel>Sync Tag</ControlLabel>
              <FormControl
                data-test="input-synctag"
                name="sync_tag"
                value={device.sync_tag}
                onChange={this.onChange}
                disabled={disabled || [0, 1].includes(device.synchronisation_method)}
              />
            </div>
            <div>
              <ControlLabel>NTP Server</ControlLabel>
              <FormControl
                data-test="input-ntpserver"
                name="time_sync"
                value={device.time_sync}
                onChange={this.onChange}
                disabled={disabled || device.synchronisation_method !== 1}
              />
            </div>
            <div>
              <ControlLabel>Seamless Playback</ControlLabel>
              <Checkbox
                data-test="checkbox-videowall"
                name="vw_enabled"
                checked={device.vw_enabled}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Panel Left</ControlLabel>
              <FormControl
                data-test="input-vwleft"
                name="vw_left"
                value={device.vw_left}
                onChange={this.onChange}
                disabled={disabled || !device.vw_enabled}
              />
            </div>
            <div>
              <ControlLabel>Panel Top</ControlLabel>
              <FormControl
                data-test="input-vwtop"
                name="vw_top"
                value={device.vw_top}
                onChange={this.onChange}
                disabled={disabled || !device.vw_enabled}
              />
            </div>
            <div>
              <ControlLabel>Panel Width</ControlLabel>
              <FormControl
                data-test="input-vwpanelwidth"
                name="vw_width"
                value={device.vw_width}
                onChange={this.onChange}
                disabled={disabled || !device.vw_enabled}
              />
            </div>
            <div>
              <ControlLabel>Panel Height</ControlLabel>
              <FormControl
                data-test="input-vwpanelheight"
                name="vw_height"
                value={device.vw_height}
                onChange={this.onChange}
                disabled={disabled || !device.vw_enabled}
              />
            </div>
            <div>
              <ControlLabel>Wall Width</ControlLabel>
              <FormControl
                data-test="input-vwwallwidth"
                name="vw_wall_width"
                value={device.vw_wall_width}
                onChange={this.onChange}
                disabled={disabled || !device.vw_enabled}
              />
            </div>
            <div>
              <ControlLabel>Wall Height</ControlLabel>
              <FormControl
                data-test="input-vwwallheight"
                name="vw_wall_height"
                value={device.vw_wall_height}
                onChange={this.onChange}
                disabled={disabled || !device.vw_enabled}
              />
            </div>
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  getGeoLocation = () => {
    const { device, disabled } = this.props;
    const address = device.geo_geocode_address || '';
    const hasAddress = Boolean(address);
    const locationMode =
      device.geo_location_mode || (device.geo_address_line ? 'address' : 'postal');
    const isAddressMode = locationMode === 'address';

    return (
      <Col md={6} lg={4}>
        <Panel>
          <Panel.Heading>Geo Location</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>Location Input</ControlLabel>
              <FormControl
                data-test="select-geolocationmode"
                componentClass="select"
                name="geo_location_mode"
                value={locationMode}
                onChange={this.onGeoLocationMode}
                disabled={disabled}
              >
                <option value="postal">Postal code</option>
                <option value="address">Address</option>
              </FormControl>
            </div>
            <div>
              <ControlLabel>Country Code</ControlLabel>
              <FormControl
                data-test="input-geocountry"
                name="geo_country_code"
                value={device.geo_country_code || ''}
                onChange={this.onGeoCountryCode}
                disabled={disabled}
                maxLength={2}
                placeholder="e.g. GB"
              />
            </div>
            {isAddressMode ? (
              <div>
                <ControlLabel>Address</ControlLabel>
                <FormControl
                  data-test="input-geoaddress"
                  name="geo_address_line"
                  value={device.geo_address_line || ''}
                  onChange={this.onChange}
                  disabled={disabled}
                  maxLength={255}
                  placeholder="e.g. The Exchange Station Parade, Harrogate"
                />
              </div>
            ) : (
              <div>
                <ControlLabel>Postal Code</ControlLabel>
                <FormControl
                  data-test="input-geopostalcode"
                  name="geo_postal_code"
                  value={device.geo_postal_code || ''}
                  onChange={this.onChange}
                  disabled={disabled}
                  maxLength={32}
                  placeholder="e.g. HG1 1TS"
                />
              </div>
            )}
            {hasAddress && (
              <div>
                <ControlLabel>Geo Address</ControlLabel>
                <FormControl
                  className="text-light"
                  data-test="geo-address"
                  value={address}
                  disabled
                />
              </div>
            )}
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  getPowerSchedules = () => {
    const { device, device_type, disabled, onChange } = this.props;

    return (
      <Col md={6} lg={4}>
        <PowerSchedules
          device={device}
          device_type={device_type}
          disabled={disabled}
          onChange={onChange}
          onChangeCheckbox={this.onChangeCheckbox}
        />
      </Col>
    );
  };

  getDiagnostics = () => {
    const { device, disabled } = this.props;

    return (
      <Col md={6} lg={4}>
        <Panel>
          <Panel.Heading>Diagnostics</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>Overlay Diagnostics</ControlLabel>
              <Checkbox
                data-test="checkbox-diagnostics"
                name="debug_toast"
                checked={device.debug_toast}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Save Errors</ControlLabel>
              <Checkbox
                data-test="checkbox-saveerrors"
                name="save_errors"
                checked={device.save_errors}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Download Progress</ControlLabel>
              <Checkbox
                data-test="checkbox-progress"
                name="show_progress"
                checked={device.show_progress}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Error Marker</ControlLabel>
              <Checkbox
                data-test="checkbox-errormarker"
                name="error_marker"
                checked={device.error_marker}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Network Marker</ControlLabel>
              <Checkbox
                data-test="checkbox-networkstatusmarker"
                name="network_status_marker"
                checked={device.network_status_marker}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Statistics Overlay</ControlLabel>
              <Checkbox
                data-test="checkbox-statistics"
                name="show_stats"
                checked={device.show_stats}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Network Watchdog</ControlLabel>
              <Checkbox
                data-test="checkbox-networkwatchdog"
                name="network_watchdog"
                checked={device.network_watchdog}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Healthcheck Config</ControlLabel>
              {!device.healthcheck_config && <span>{device.healthcheck_config_name}</span>}
              {device.healthcheck_config && (
                <ToolTip
                  toolText={
                    <pre style={{ textAlign: 'left', fontSize: '0.7em' }}>
                      {JSON.stringify(device.healthcheck_config, null, 2)}
                    </pre>
                  }
                >
                  {device.healthcheck_config_name}
                </ToolTip>
              )}
            </div>
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  getOverlays = () => {
    const { device, disabled } = this.props;

    return (
      <Col md={6} lg={4}>
        <Panel>
          <Panel.Heading>Overlays</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>Overlay Source</ControlLabel>
              <select
                data-test="select-overlaysource"
                name="overlay_source_type"
                value={device.overlay_source_type}
                onChange={this.onChangeSelect}
                disabled={disabled}
              >
                {this.getOverlaySourceOptions()}
              </select>
            </div>
            <div>
              <ControlLabel>Overlay Params</ControlLabel>
              <FormControl
                data-test="input-overlayparams"
                name="overlay_source_params"
                value={device.overlay_source_params}
                onChange={this.onChange}
                disabled={disabled || !device.overlay_source_type}
              />
            </div>
            <div>
              <ControlLabel>Overlay Prefix</ControlLabel>
              <FormControl
                data-test="input-overlayprefix"
                name="overlay_prefix"
                value={device.overlay_prefix}
                onChange={this.onChange}
                disabled={disabled || !device.overlay_source_type}
              />
            </div>
            <div>
              <ControlLabel>Overlay Suffix</ControlLabel>
              <FormControl
                data-test="input-overlaysuffix"
                name="overlay_suffix"
                value={device.overlay_suffix}
                onChange={this.onChange}
                disabled={disabled || !device.overlay_source_type}
              />
            </div>
            <div>
              <ControlLabel>Overlay Alignment</ControlLabel>
              <select
                data-test="select-overlayalignment"
                name="overlay_alignment"
                value={device.overlay_alignment}
                onChange={this.onChangeSelect}
                disabled={disabled || !device.overlay_source_type}
              >
                {this.getOverlayAlignmentOptions()}
              </select>
            </div>
            <div>
              <ControlLabel>Overlay Duration</ControlLabel>
              <FormControl
                data-test="input-overlayduration"
                name="overlay_duration"
                onChange={this.onChange}
                value={device.overlay_duration}
                disabled={disabled || !device.overlay_source_type}
              />
            </div>
            <div>
              <ControlLabel>Overlay Speech</ControlLabel>
              <Checkbox
                data-test="checkbox-speech"
                name="overlay_generate_speech"
                checked={device.overlay_generate_speech}
                onChange={this.onChangeCheckbox}
                disabled={disabled || !device.overlay_source_type}
              />
            </div>
            <div>
              <ControlLabel>Speech Prefix</ControlLabel>
              <FormControl
                data-test="input-speechprefix"
                name="overlay_speech_prefix"
                value={device.overlay_speech_prefix}
                onChange={this.onChange}
                disabled={
                  disabled || !device.overlay_source_type || !device.overlay_generate_speech
                }
              />
            </div>
            <div>
              <ControlLabel>Speech Suffix</ControlLabel>
              <FormControl
                data-test="input-speechsuffix"
                name="overlay_speech_suffix"
                value={device.overlay_speech_suffix}
                onChange={this.onChange}
                disabled={
                  disabled || !device.overlay_source_type || !device.overlay_generate_speech
                }
              />
            </div>
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  getExternal = () => {
    const { device, disabled, original } = this.props;
    const { isMulticastAddressValid, isMulticastPortValid } = this.state;

    return (
      <Col md={6} lg={4}>
        <Panel>
          <Panel.Heading>External</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>3rd-party updates</ControlLabel>
              <Checkbox
                data-test="checkbox-externalupdate"
                name="allow_external_update"
                checked={device.allow_external_update}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Broadcast</ControlLabel>
              <Checkbox
                data-test="checkbox-broadcast"
                name="broadcast"
                checked={device.broadcast}
                onChange={this.onChangeCheckbox}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>Broadcast Port</ControlLabel>
              <FormControl
                data-test="input-broadcastport"
                name="broadcast_port"
                value={device.broadcast_port}
                onChange={this.onChange}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlLabel>
                <ToolTip
                  toolText="IPv4: 224.0.0.0-239.255.255.255 or IPv6: ff00::/8 (multicast only)"
                  infoIcon
                >
                  Multicast Address
                </ToolTip>
              </ControlLabel>
              <FormControl
                data-test="input-multicastaddress"
                name="multicast_address"
                value={device.multicast_address}
                onChange={this.onMulticastAddress}
                disabled={disabled}
                placeholder={`Current: ${original.multicast_address}`}
              />
              {!isMulticastAddressValid && (
                <span className="text-danger">
                  Enter a multicast IP (IPv4 224-239.*.*.* or IPv6 ff00::/8).
                </span>
              )}
            </div>
            <div>
              <ControlLabel>
                <ToolTip toolText="UDP port (1-65535)" infoIcon>
                  Multicast Port
                </ToolTip>
              </ControlLabel>
              <FormControl
                data-test="input-multicastport"
                name="multicast_port"
                type="number"
                min="1"
                max="65535"
                value={device.multicast_port}
                onChange={this.onMulticastPort}
                disabled={disabled}
                placeholder={`Current: ${original.multicast_port}`}
              />
              {!isMulticastPortValid && (
                <span className="text-danger">Invalid port. Use 1-65535.</span>
              )}
            </div>
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  getButtons = () => {
    const { auth, disabled, onCancel } = this.props;
    const p = getPermissions(auth);

    if (disabled) {
      return null;
    }

    return (
      <div className="form-buttons">
        <Button data-test="button-cancel" onClick={onCancel}>
          Cancel
        </Button>
        {p.deviceManager && (
          <Button data-test="button-update" bsStyle="success" type="submit">
            Update Device
          </Button>
        )}
      </div>
    );
  };

  getFusion = () => {
    const { device, disabled, device_type } = this.props;
    // if the logic gets more complicated I'd suggest moving it into a component
    const supportList = [DeviceType.FUSION_H76, DeviceType.S35];
    const isSupported = supportList.includes(device_type);

    return (
      <Col md={6} lg={4}>
        <Panel>
          <Panel.Heading>SignFusion Panel</Panel.Heading>
          <Panel.Body>
            <div>
              <ControlLabel>
                <ToolTip
                  toolText={
                    <div>
                      {!isSupported && (
                        <Alert bsStyle="warning">
                          Not supported on <b>{device_type}</b> device.
                        </Alert>
                      )}
                      Currently {supportList.join(', ')} only
                    </div>
                  }
                  infoIcon
                >
                  Enabled
                </ToolTip>
              </ControlLabel>
              <Checkbox
                data-test="checkbox-fusion"
                name="fusion_1_enabled"
                checked={device.fusion_1_enabled}
                onChange={this.onChangeCheckbox}
                disabled={disabled || !isSupported}
              />
            </div>
            <div>
              <ControlLabel>Left</ControlLabel>
              <FormControl
                data-test="input-fsleft"
                name="fusion_1_left"
                value={device.fusion_1_left}
                onChange={this.onChange}
                disabled={disabled || !isSupported || !device.fusion_1_enabled}
              />
            </div>
            <div>
              <ControlLabel>Top</ControlLabel>
              <FormControl
                data-test="input-fstop"
                name="fusion_1_top"
                value={device.fusion_1_top}
                onChange={this.onChange}
                disabled={disabled || !isSupported || !device.fusion_1_enabled}
              />
            </div>
            <div>
              <ControlLabel>Width</ControlLabel>
              <FormControl
                data-test="input-fswidth"
                name="fusion_1_width"
                value={device.fusion_1_width}
                onChange={this.onChange}
                disabled={disabled || !isSupported || !device.fusion_1_enabled}
              />
            </div>
            <div>
              <ControlLabel>Height</ControlLabel>
              <FormControl
                data-test="input-fsheight"
                name="fusion_1_height"
                value={device.fusion_1_height}
                onChange={this.onChange}
                disabled={disabled || !isSupported || !device.fusion_1_enabled}
              />
            </div>
            <div>
              <ControlLabel>Audio Source</ControlLabel>
              <select
                data-test="select-fsaudiosource"
                name="fusion_1_audio_source"
                value={device.fusion_1_audio_source}
                onChange={this.onChangeSelect}
                disabled={disabled || !isSupported || !device.fusion_1_enabled}
              >
                {this.getFusionAudioSourceOptions()}
              </select>
            </div>
          </Panel.Body>
        </Panel>
      </Col>
    );
  };

  render() {
    const { auth, device, disabled } = this.props;
    const p = getPermissions(auth);

    if (!p.atLeastTierProOrSuperUser || !device.id) {
      return null;
    }

    return (
      <form className={disabled ? 'disabled' : ''} onSubmit={this.submit}>
        <Grid fluid>
          <Row>
            {this.getGeneral()}
            {this.getVideoWall()}
            <Clearfix visibleMdBlock />
            {this.getGeoLocation()}
            <Clearfix visibleLgBlock />
            {this.getPowerSchedules()}
            <Clearfix visibleMdBlock />
            {this.getDiagnostics()}
            {this.getFusion()}
            <Clearfix visibleMdBlock />
            <Clearfix visibleLgBlock />
            {this.getExternal()}
            {this.getOverlays()}
            <Clearfix visibleMdBlock />
            <Clearfix visibleLgBlock />
          </Row>
        </Grid>
        {this.getButtons()}
      </form>
    );
  }
}

DeviceForm.propTypes = {
  auth: PropTypes.object.isRequired,
  locations: PropTypes.array.isRequired,
  original: PropTypes.object.isRequired,
  device: PropTypes.object.isRequired,
  device_type: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onShowLocationSelector: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  getFullLocation: PropTypes.func.isRequired,
  userLocation: PropTypes.string.isRequired,
};
